import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api/config';
import { downloadAsCSV } from '@/lib/utils/csv';

interface IOC {
  ioc_id: string;
  ioc_type: string;
  group_id: string;
}

interface RelatedIOCsProps {
  groupId: string;
}

// Helper function to group IOCs by type
function groupIOCsByType(iocs: IOC[]): Record<string, IOC[]> {
  const grouped = iocs.reduce((acc, ioc) => {
    if (!acc[ioc.ioc_type]) {
      acc[ioc.ioc_type] = [];
    }
    acc[ioc.ioc_type].push(ioc);
    return acc;
  }, {} as Record<string, IOC[]>);

  // Ensure FileHash-SHA256 is first if it exists
  const ordered: Record<string, IOC[]> = {};
  if (grouped['FileHash-SHA256']) {
    ordered['FileHash-SHA256'] = grouped['FileHash-SHA256'];
    delete grouped['FileHash-SHA256'];
  }

  // Add remaining types
  return { ...ordered, ...grouped };
}

export function RelatedIOCs({ groupId }: RelatedIOCsProps) {
  const [iocs, setIocs] = useState<IOC[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultTab, setDefaultTab] = useState<string>('');
  
  const handleDownloadCSV = () => {
    // Transform IOCs for CSV download to include the ioc_id as the value
    const csvData = iocs.map(ioc => ({
      value: ioc.ioc_id,
      type: ioc.ioc_type
    }));
    downloadAsCSV(csvData, `iocs-${groupId}.csv`);
  };

  useEffect(() => {
    async function fetchIOCs() {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/groups/${groupId}/iocs`);
        const iocData = response.data;
        setIocs(iocData);

        // Set default tab to FileHash-SHA256 if available, otherwise first type
        const groupedIOCs = groupIOCsByType(iocData);
        const firstType = Object.keys(groupedIOCs)[0] || '';
        setDefaultTab(firstType);
      } catch (err) {
        console.error('Error fetching IOCs:', err);
        setError('Failed to load IOCs');
      } finally {
        setIsLoading(false);
      }
    }

    fetchIOCs();
  }, [groupId]);

  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading IOCs...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!iocs.length) {
    return null;
  }

  const groupedIOCs = groupIOCsByType(iocs);

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Related IOCs</CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleDownloadCSV}
          >
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs defaultValue={defaultTab}>
          <div className="border-b mb-4">
            <TabsList className="h-auto flex-wrap gap-2 p-4">
              {Object.entries(groupedIOCs).map(([type, typeIocs]) => (
                <TabsTrigger key={type} value={type}>
                  <span className="flex items-center">
                    {type}
                    <Badge variant="secondary" className="ml-1">
                      {typeIocs.length}
                    </Badge>
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {Object.entries(groupedIOCs).map(([type, typeIocs]) => (
            <TabsContent key={type} value={type}>
              <ScrollArea className="h-[400px] rounded-md border p-4">
                <div className="flex flex-wrap gap-2">
                  {typeIocs.map((ioc) => (
                    <div
                      key={ioc.ioc_id}
                      className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors bg-accent text-accent-foreground hover:bg-accent/80"
                      onClick={() => {
                        navigator.clipboard.writeText(ioc.ioc_id);
                      }}
                      title="Click to copy"
                    >
                      {ioc.ioc_id}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}